import PropTypes from 'prop-types'
import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import routes from 'constants/router'
import findMatchedRoutes, { getLastMatchedRoute } from 'utils/findMatchedRoutes'
import { ROUTES } from 'constants/routes'
import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useAuthState } from 'modules/Auth/hooks/useAuthState'
import { useEmailConfirmedBroadcast } from 'modules/Auth/hooks/useEmailConfirmedBroadcast'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Trans } from 'react-i18next'
import { PRELOAD_POOL, startBackgroundPreload } from 'store/domains/utils'
import { isGuestAccess, isPrivateAccess, shouldBootstrapSession } from 'utils/routeAccess'
import { SET_DATA } from 'modules/Auth/types'

const Panels = lazy(() => import('modules/Panels'))

const AuthProvider = ({ children }) => {
  const dispatch = useDispatch()
  const { startSessionOrLogout, showNotification } = useAuth()
  const { user, isAuthenticated } = useAuthState()
  const pathGen = useGeneratePathI18n()
  const location = useLocation()

  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'))
  const hasShownUnconfirmedEmailNotification = useRef(false)
  const sessionBootstrapRequested = useRef(false)

  useEmailConfirmedBroadcast()

  const matchedRoutes = useMemo(
    () => findMatchedRoutes(routes, location.pathname),
    [location.pathname],
  )

  const matchedRouteAccess = getLastMatchedRoute(matchedRoutes)?.access

  const isPrivateRoute = useMemo(
    () => isPrivateAccess(matchedRouteAccess),
    [matchedRouteAccess],
  )

  const isGuestRoute = useMemo(
    () => isGuestAccess(matchedRouteAccess),
    [matchedRouteAccess],
  )

  const onStorageHandler = ({ key, newValue }) =>
    key === 'refresh_token' && setRefreshToken(newValue || '')

  useEffect(() => {
    window.addEventListener('storage', onStorageHandler)

    return () => {
      window.removeEventListener('storage', onStorageHandler)
    }
  }, [])

  useEffect(() => {
    const hasRefreshToken = Boolean(localStorage.getItem('refresh_token'))

    if (isGuestRoute) {
      sessionBootstrapRequested.current = false
      dispatch({ type: SET_DATA, payload: { loading: false } })
      return
    }

    if (hasRefreshToken) {
      if (!isAuthenticated && !sessionBootstrapRequested.current) {
        sessionBootstrapRequested.current = true
        startSessionOrLogout()
      }
      return
    }

    sessionBootstrapRequested.current = false

    if (shouldBootstrapSession(matchedRoutes)) {
      startSessionOrLogout()
      return
    }

    dispatch({ type: SET_DATA, payload: { loading: false } })
  }, [refreshToken, isAuthenticated, isGuestRoute, matchedRoutes, startSessionOrLogout, dispatch])

  useEffect(() => {
    if (user?.is_email_confirmed === true) {
      hasShownUnconfirmedEmailNotification.current = false
      return
    }

    if (user?.is_email_confirmed !== false || hasShownUnconfirmedEmailNotification.current) {
      return
    }

    hasShownUnconfirmedEmailNotification.current = true
    showNotification({
      type: 'warning',
      message: (
        <Trans i18nKey="user::settings.profile.email.notification.disapproved">
          Подтвердите адрес электронной почты. Статус подтверждения можно смотреть в{' '}
          <Link to={pathGen(ROUTES.USER_SETTINGS)}>личном кабинете</Link>
        </Trans>
      ),
    })
  }, [user?.is_email_confirmed, user?.id, pathGen, showNotification])

  const matchedRoute = getLastMatchedRoute(matchedRoutes)

  useEffect(() => {
    if (!isAuthenticated || !isPrivateRoute) {
      return
    }

    let cancelled = false
    let timeoutId = null
    let cancelPreload = () => {}

    timeoutId = setTimeout(() => {
      if (cancelled) {
        return
      }
      cancelPreload = startBackgroundPreload({
        skipRouteKey: matchedRoute?.key,
        pool: PRELOAD_POOL.PRIVATE,
      })
    }, 500)

    return () => {
      cancelled = true
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      cancelPreload()
    }
  }, [isAuthenticated, isPrivateRoute, matchedRoute?.key])

  useEffect(() => {
    if (isAuthenticated || !isGuestRoute) {
      return
    }

    let cancelled = false
    let timeoutId = null
    let cancelPreload = () => {}

    timeoutId = setTimeout(() => {
      if (cancelled) {
        return
      }
      cancelPreload = startBackgroundPreload({
        skipRouteKey: matchedRoute?.key,
        pool: PRELOAD_POOL.GUEST,
      })
    }, 500)

    return () => {
      cancelled = true
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      cancelPreload()
    }
  }, [isAuthenticated, isGuestRoute, matchedRoute?.key])

  return (
    <>
      {children}
      {isAuthenticated && (
        <Suspense fallback={null}>
          <Panels />
        </Suspense>
      )}
    </>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node,
}

export default AuthProvider
